import { GeneratedProductApproval } from '@texas/api/endpoints/productApprovalsApi';
import { SimpleContact } from '@texas/api/endpoints/contactsApi';

export interface ApprovalValues {
  supplierContact: SimpleContact | null;
  customerContact: SimpleContact | null;
  sampleReadyDate: string;
}

export interface ApprovalDraft extends ApprovalValues {
  sample: GeneratedProductApproval;
  ignored: boolean;
  overrides: Partial<Record<keyof ApprovalValues, boolean>>;
}

export interface SharedApprovalValues {
  suppliers: Record<number, SimpleContact | null>;
  customers: Record<number, SimpleContact | null>;
  sampleReadyDate: string | null;
}

export function createApprovalDrafts(
  samples: GeneratedProductApproval[],
): ApprovalDraft[] {
  return samples.map((sample) => ({
    sample,
    supplierContact: sample.supplierContact,
    customerContact: sample.customerContact,
    sampleReadyDate: sample.expectedReadyDate?.slice(0, 10) ?? '',
    ignored: false,
    overrides: {},
  }));
}

export function getSharedApprovalValues(
  drafts: ApprovalDraft[],
): SharedApprovalValues {
  const contacts = (
    field: 'supplierContact' | 'customerContact',
    idField: 'supplierId' | 'customerId',
  ) => {
    const result: Record<number, SimpleContact | null> = {};
    for (const draft of drafts) {
      const id = draft.sample[idField];
      if (!(id in result)) {
        result[id] = draft[field];
      } else if (result[id]?.id !== draft[field]?.id) {
        result[id] = null;
      }
    }
    return result;
  };
  const firstDate = drafts[0]?.sampleReadyDate ?? null;
  return {
    suppliers: contacts('supplierContact', 'supplierId'),
    customers: contacts('customerContact', 'customerId'),
    sampleReadyDate: drafts.every(
      (draft) => draft.sampleReadyDate === firstDate,
    )
      ? firstDate
      : null,
  };
}

export function applySharedApprovalValue<K extends keyof ApprovalValues>(
  drafts: ApprovalDraft[],
  field: K,
  value: ApprovalValues[K],
  overwrite: boolean,
  referenceId?: number,
): ApprovalDraft[] {
  return drafts.map((draft) => {
    const matches =
      field === 'supplierContact'
        ? draft.sample.supplierId === referenceId
        : field === 'customerContact'
        ? draft.sample.customerId === referenceId
        : true;
    if (!matches || (draft.overrides[field] && !overwrite)) {
      return draft;
    }
    return {
      ...draft,
      [field]: value,
      overrides: { ...draft.overrides, [field]: false },
    };
  });
}

export function getApprovalIssues(
  draft: ApprovalDraft,
): (keyof ApprovalValues)[] {
  if (draft.ignored) {
    return [];
  }
  const issues: (keyof ApprovalValues)[] = [];
  if (!draft.supplierContact) {
    issues.push('supplierContact');
  }
  if (!draft.customerContact) {
    issues.push('customerContact');
  }
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(draft.sampleReadyDate) ||
    !Number.isFinite(new Date(draft.sampleReadyDate).getTime()) ||
    new Date(draft.sampleReadyDate).toISOString().slice(0, 10) !==
      draft.sampleReadyDate
  ) {
    issues.push('sampleReadyDate');
  }
  return issues;
}
